import React, { useContext } from 'react'
import { CircleLoader, FullBlockLoader } from '@deal/components'
import RequestPushNotificationsAfterDenial from '#src/components/RequestPushNotificationsAfterDenial'
import RequestPushNotifications from '#src/components/RequestPushNotifications'
import { InterstitialProps } from '#src/components/Path/Interstitials'
import {
  WebMessageEventEmitter,
  WebMessageEventEmitterContext
} from '#src/containers/ReceiveMessageFromNative'
import {
  RequestPushNotificationPermissionResponseMessage,
  GetNotificationSettingsAuthorizationStatusResponseMessage,
  NotificationSettingsAuthorizationStatus,
  GetFcmTokenResponseMessage
} from '#src/services/dealNativeBridge'
import {
  SubmitPushNotificationAuthorizedStatusMutationFn,
  useSubmitPushNotifcationFcmTokenMutation,
  useSubmitPushNotificationAuthorizedStatusMutation
} from '#src/generated/graphql'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import uuidv4 from 'uuid/v4'
import { usePathContext } from '../../PathContext'

interface RequestPushNotificationProps {
  emitter: WebMessageEventEmitter
  onSubmitPushNotificationFCMToken: (fcmToken: string) => void
  submitPushNotificationAuthorizedStatus: SubmitPushNotificationAuthorizedStatusMutationFn
}

enum RequestPushNotificationAuthorizationValues {
  DENIED,
  AUTHORIZED
}

interface RequestPushNotificationsState {
  loading: boolean
  didDenyPushNotifications: boolean
}

interface RequestPushNotificationsInterstitialProps extends InterstitialProps {
  onPageSubmitted: () => void
  pathFlowId: string
}

class RequestPushNotificationsInterstitial extends React.Component<
  RequestPushNotificationsInterstitialProps & RequestPushNotificationProps & WithAnalyticsProps,
  RequestPushNotificationsState
> {
  constructor(props: RequestPushNotificationsInterstitialProps & RequestPushNotificationProps) {
    super(props)

    this.state = {
      loading: true,
      didDenyPushNotifications: false
    }
    this.handleEnablePushNotifications = this.handleEnablePushNotifications.bind(this)
    this.handleDenyPushNotifications = this.handleDenyPushNotifications.bind(this)
  }

  public componentDidMount() {
    this.props.emitter
      .requestResponse<GetNotificationSettingsAuthorizationStatusResponseMessage>(
        {
          type: 'getNotificationSettingsAuthorizationStatusRequest',
          messageId: uuidv4()
        },
        'getNotificationSettingsAuthorizationStatusResponse'
      )
      .then(message => {
        /**
         * Only skip this page if we've previously authorzed or denied the request. We can
         * eventually improve this with a design that encourages them to manually go enable
         * the notifications in their app settings.
         */
        if (message.status === NotificationSettingsAuthorizationStatus.denied) {
          if (this.props.analytics) {
            this.props.analytics.events.pushNotificationsDeniedOnLoad()
          }

          this.submitPushNotificationAuthorizedStatus(
            RequestPushNotificationAuthorizationValues.DENIED
          ).then(() => {
            this.props.onPageSubmitted()
          })
        } else if (message.status === NotificationSettingsAuthorizationStatus.authorized) {
          this.handleGetTokenAndSubmitPage()

          if (this.props.analytics) {
            this.props.analytics.events.pushNotificationsAllowedOnLoad()
          }
        } else {
          this.setState({ loading: false })
        }
      })
  }

  public submitPushNotificationAuthorizedStatus(
    status: RequestPushNotificationAuthorizationValues
  ) {
    let valueString

    switch (status) {
      case RequestPushNotificationAuthorizationValues.AUTHORIZED:
        valueString = 'AUTHORIZED'
        break

      // Default to denied so we don't brick the app
      case RequestPushNotificationAuthorizationValues.DENIED:
      default:
        valueString = 'DENIED'
    }

    return this.props.submitPushNotificationAuthorizedStatus!({
      variables: {
        input: {
          pathFlowId: this.props.pathFlowId,
          questionName: 'push-notification-authorization',
          value: [valueString]
        }
      }
    })
  }

  public handleGetTokenAndSubmitPage() {
    this.props.emitter
      .requestResponse<GetFcmTokenResponseMessage>(
        {
          type: 'getFcmTokenRequest',
          messageId: uuidv4()
        },
        'getFcmTokenResponse'
      )
      .then(message => {
        if (this.props.analytics) {
          this.props.analytics.events.pushNotificationsAllowed()
        }

        this.submitPushNotificationAuthorizedStatus(
          RequestPushNotificationAuthorizationValues.AUTHORIZED
        ).then(() => {
          this.props.onSubmitPushNotificationFCMToken(message.fcmToken)
        })
      })
  }

  public handleEnablePushNotifications() {
    this.props.emitter
      .requestResponse<RequestPushNotificationPermissionResponseMessage>(
        {
          type: 'requestPushNotificationPermissionRequest',
          messageId: uuidv4()
        },
        'requestPushNotificationPermissionResponse'
      )
      .then(message => {
        if (message.success) {
          // Run query to get FCM token and submit path page and continue
          this.handleGetTokenAndSubmitPage()
        } else {
          if (this.props.analytics) {
            this.props.analytics.events.pushNotificationsDenied()
          }

          // Another opportunity to remind them they can enable the notifications themselves later
          this.submitPushNotificationAuthorizedStatus(
            RequestPushNotificationAuthorizationValues.DENIED
          ).finally(() => this.props.onPageSubmitted())
        }
      })
      .catch(() => this.props.onPageSubmitted())
  }

  public handleDenyPushNotifications() {
    // They denied the request, show a different component
    this.setState({
      didDenyPushNotifications: true
    })

    if (this.props.analytics) {
      this.props.analytics.events.pushNotificationsDenied()
    }
  }

  public render() {
    const { loading, didDenyPushNotifications } = this.state
    const { onPageSubmitted } = this.props

    if (didDenyPushNotifications) {
      return (
        <RequestPushNotificationsAfterDenial
          onDissmissPage={() => {
            this.submitPushNotificationAuthorizedStatus(
              RequestPushNotificationAuthorizationValues.DENIED
            ).finally(() => {
              onPageSubmitted()
            })
          }}
          onAcceptNotifcations={this.handleEnablePushNotifications}
        />
      )
    } else if (loading) {
      return <FullBlockLoader loader={CircleLoader} />
    }

    return (
      <RequestPushNotifications
        onAcceptNotifcations={this.handleEnablePushNotifications}
        onDenyNotificaftions={this.handleDenyPushNotifications}
      />
    )
  }
}

const WrappedRequestPushNotification: React.SFC<InterstitialProps & WithAnalyticsProps> = ({
  ...rest
}) => {
  const pathContext = usePathContext()
  const eventEmitter = useContext(WebMessageEventEmitterContext)
  const [submitPushNotificationFCMToken] = useSubmitPushNotifcationFcmTokenMutation({
    onCompleted: () => pathContext.onPageSubmitted()
  })
  const [
    submitPushNotificationAuthorizedStatus
  ] = useSubmitPushNotificationAuthorizedStatusMutation()

  if (eventEmitter) {
    return (
      <RequestPushNotificationsInterstitial
        emitter={eventEmitter}
        submitPushNotificationAuthorizedStatus={submitPushNotificationAuthorizedStatus}
        onSubmitPushNotificationFCMToken={fcmToken => {
          submitPushNotificationFCMToken({
            variables: {
              input: {
                pathFlowId: pathContext.pathFlow.id,
                questionName: 'fcm-token',
                value: fcmToken
              }
            }
          })
        }}
        onPageSubmitted={pathContext.onPageSubmitted}
        pathFlowId={pathContext.pathFlow.id}
        {...rest}
      />
    )
  } else {
    return null
  }
}

export default withAnalytics(WrappedRequestPushNotification)
